import { formatDistance } from 'date-fns'

import { AIConversationGenerationStatus } from '../../constants/aiAgents'
import {
  API_TIMEOUT_ERROR_CODES,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_UNAUTHORIZED,
} from '../../constants/api'
import { EMPTY_STRING } from '../../constants/common'
import { EVENT_NAME_BLUR } from '../../constants/event'
import {
  isAIFinanceAccessRevokedError,
  isAISourceUnavailableError,
} from '../../utils/aiAgents'
import { sleep } from '../../utils/async'
import { isBrowserOffline, isValueNumberType } from '../../utils/common'

import {
  GRETA_AI_CHAT_BALANCE_NOT_FOUND_ERROR,
  GRETA_AI_CHAT_CRITICAL_STATUSES,
  GRETA_AI_CHAT_ERROR_TEXT_KEY,
  GRETA_AI_CHAT_RETRYABLE_STATUSES,
  GRETA_OFFLINE_RECHECK_DELAY_MS,
  GRETA_SILENT_FOCUS_ATTRIBUTE,
  GretaAIChatFeedback,
  GretaAIChatMessageRole,
  GretaAIChatMode,
  GretaAIChatRowStatus,
  GretaAIChatStatus,
} from './GretaAIChat.constants'

// Re-check offline once for a response-less failure, not an HTTP error or timeout.
async function getIsGretaAIChatOfflineError(reason) {
  if (isBrowserOffline()) {
    return true
  }

  if (!reason?.response) {
    await sleep(GRETA_OFFLINE_RECHECK_DELAY_MS)

    return isBrowserOffline()
  }

  return false
}

export function getIsGretaAIChatAssistantRole(role) {
  return role === GretaAIChatMessageRole.Assistant
}

export function getIsGretaAIChatChatRunning(chat) {
  return chat.generationStatus === AIConversationGenerationStatus.Running
}

export function getIsGretaAIChatLoading(status) {
  return status === GretaAIChatStatus.Loading
}

export function getIsGretaAIChatThinking(status) {
  return status === GretaAIChatStatus.Thinking
}

export function getIsGretaAIChatIdle(status) {
  return status === GretaAIChatStatus.Idle
}

export function getIsGretaAIChatFeedTurnInFlight(
  conversationId,
  { getIsTimeoutStopPending, getIsTurnInFlight }
) {
  return (
    getIsTurnInFlight(conversationId) &&
    !getIsTimeoutStopPending(conversationId)
  )
}

export function getIsGretaAIChatTimeout(status) {
  return status === GretaAIChatStatus.Timeout
}

export function getIsGretaAIChatOffline(status) {
  return status === GretaAIChatStatus.Offline
}

export function getIsGretaAIChatRetryableStatus(status) {
  return GRETA_AI_CHAT_RETRYABLE_STATUSES.includes(status)
}

export function getIsGretaAIChatCriticalStatus(status) {
  return GRETA_AI_CHAT_CRITICAL_STATUSES.includes(status)
}

export function getIsGretaAIChatBalanceExhausted(response) {
  const balance = response?.data?.balance_tokens

  return isValueNumberType(balance) && balance <= 0
}

export function getIsGretaAIChatPackageLocked(error) {
  return (
    error?.response?.status === HTTP_STATUS_NOT_FOUND &&
    error?.response?.data?.errors === GRETA_AI_CHAT_BALANCE_NOT_FOUND_ERROR
  )
}

export function getGretaAIChatVisibleSuggestedPrompts(prompts) {
  return prompts.filter(prompt => !prompt.isHidden)
}

export function getGretaAIChatChatById(chats, id) {
  return chats.find(chat => chat.id === id) ?? null
}

export function getIsGretaAIChatDialogOpen({ chatAction, projectAction }) {
  return Boolean(chatAction || projectAction)
}

export function getIsGretaAIChatRowGenerating(rowStatus) {
  return rowStatus === GretaAIChatRowStatus.Generating
}

export function getIsGretaAIChatRowNewReply(rowStatus) {
  return rowStatus === GretaAIChatRowStatus.NewReply
}

export function getHasGretaAIChatUnread(chatStatusById, chats) {
  return chats.some(chat =>
    getIsGretaAIChatRowNewReply(chatStatusById[chat.id])
  )
}

export function getGretaAIChatErrorTextKey(status) {
  return GRETA_AI_CHAT_ERROR_TEXT_KEY[status] ?? null
}

export function formatGretaAIChatTimeRelative(timestamp, now, locale) {
  const updatedDate = new Date(timestamp)
  const currentDate = new Date(now)

  return formatDistance(
    updatedDate,
    new Date(Math.max(updatedDate, currentDate)),
    {
      addSuffix: true,
      locale,
    }
  )
}

export function getIsGretaAIChatTimeoutError(reason) {
  return API_TIMEOUT_ERROR_CODES.includes(reason?.code)
}

export async function getGretaAIChatErrorStatus(reason) {
  if (getIsGretaAIChatTimeoutError(reason)) {
    return GretaAIChatStatus.Timeout
  }

  if (await getIsGretaAIChatOfflineError(reason)) {
    return GretaAIChatStatus.Offline
  }

  if (isAIFinanceAccessRevokedError(reason)) {
    return GretaAIChatStatus.SourceRevoked
  }

  if (isAISourceUnavailableError(reason)) {
    return GretaAIChatStatus.SourceUnavailable
  }

  const httpStatus = reason?.response?.status

  if (
    httpStatus === HTTP_STATUS_UNAUTHORIZED ||
    httpStatus === HTTP_STATUS_FORBIDDEN
  ) {
    return GretaAIChatStatus.SessionExpired
  }

  return GretaAIChatStatus.Error
}

export function getIsGretaAIChatExpanded(mode) {
  return mode === GretaAIChatMode.Expanded
}

export function getIsGretaAIChatFeedbackDown(value) {
  return value === GretaAIChatFeedback.Down
}

export function getIsGretaAIChatFeedbackUp(value) {
  return value === GretaAIChatFeedback.Up
}

export function focusFirstGretaAIChatChatListRow(container) {
  const firstRow = container?.querySelector('button')

  if (firstRow) {
    firstRow.focus()
  }

  return Boolean(firstRow)
}

// Scripted focus() inherits keyboard modality, so mark it to suppress :focus-visible.
export function focusSilently(element) {
  if (element) {
    element.setAttribute(GRETA_SILENT_FOCUS_ATTRIBUTE, EMPTY_STRING)
    element.addEventListener(
      EVENT_NAME_BLUR,
      () => element.removeAttribute(GRETA_SILENT_FOCUS_ATTRIBUTE),
      { once: true }
    )
    element.focus()
  }
}
